/* Hallmark · component: voucher chip · genre: playful · theme: project tokens (orange --primary)
 * layout: single-line pill — ticket mark, offer, code, copy affordance, all one control
 * states: default · hover · focus · active · disabled · loading · error · success
 * Presentational only: copy state belongs to the caller, same contract as `VoucherTicket`.
 * Sized for a dense results card — fixed height, truncating code, never reflows the row.
 */
import { cn } from '@/lib/utils';
import { type AvailableVoucher } from '@/types/voucher';
import { Link } from '@inertiajs/react';
import { AlertTriangle, CheckCheck, Copy, Loader2, Ticket } from 'lucide-react';
import { type VoucherCopyState } from './voucher-ticket';

export type VoucherChipVoucher = Pick<AvailableVoucher, 'code' | 'discount_label'>;

interface VoucherChipProps {
  voucher: VoucherChipVoucher;
  className?: string;
  copyState?: VoucherCopyState;
  /** Omitted — the chip still renders, disabled, so the card layout stays honest. */
  onCopy?: (code: string) => void;
  /** Omitted — no detail link, which is what a preview wants. */
  detailHref?: string;
}

export function VoucherChip({ voucher, className, copyState = 'idle', onCopy, detailHref }: VoucherChipProps) {
  const isDisabled = !onCopy || copyState === 'copying' || !voucher.code;

  const StateIcon = {
    idle: Copy,
    copying: Loader2,
    copied: CheckCheck,
    error: AlertTriangle,
  }[copyState];

  const stateLabel = {
    idle: 'Copy',
    copying: 'Đang copy',
    copied: 'Đã copy',
    error: 'Thử lại',
  }[copyState];

  const chip = (
    <button
      type="button"
      disabled={isDisabled}
      onClick={() => onCopy?.(voucher.code)}
      aria-label={`Sao chép mã ưu đãi ${voucher.code}`}
      className={cn(
        // Fixed height + min-w-0 so a long code truncates instead of pushing the
        // price row out of the card.
        'group inline-flex h-8 max-w-full min-w-0 items-center gap-1.5 rounded-full border-2 border-dashed py-0 pr-1.5 pl-2.5',
        'border-primary/40 bg-primary/10 text-foreground text-xs',
        'motion-safe:transition-[background-color,border-color,transform] motion-safe:duration-150',
        // The focus ring shows instantly — it is never part of the transition above.
        'focus-visible:ring-primary/60 focus-visible:ring-[3px] focus-visible:outline-none',
        !isDisabled &&
          'hover:border-primary/70 hover:bg-primary/15 cursor-pointer motion-safe:hover:-translate-y-px motion-safe:active:translate-y-0',
        isDisabled && 'cursor-not-allowed opacity-60',
        copyState === 'copied' && 'border-green-600/60 bg-green-600/10',
        copyState === 'error' && 'border-amber-500/70 bg-amber-500/10',
        className,
      )}
    >
      <Ticket className="text-primary size-3.5 shrink-0" />

      {voucher.discount_label && <span className="shrink-0 font-semibold">Giảm {voucher.discount_label}</span>}

      <span aria-hidden className="text-muted-foreground shrink-0">
        ·
      </span>

      <code className="min-w-0 truncate font-mono font-bold tracking-wider">{voucher.code}</code>

      <span
        className={cn(
          'ml-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
          'text-primary bg-white/70',
          copyState === 'copied' && 'bg-green-600 text-white',
          copyState === 'error' && 'bg-amber-500 text-white',
        )}
      >
        <StateIcon className={cn('size-3', copyState === 'copying' && 'motion-safe:animate-spin')} />
        {stateLabel}
      </span>
    </button>
  );

  if (!detailHref) {
    return chip;
  }

  return (
    <span className="inline-flex max-w-full min-w-0 items-center gap-2">
      {chip}
      {/* C3 typographic link. Copy stays the primary action on this surface — the
          visitor is already looking at the thing the code applies to. */}
      <Link
        href={detailHref}
        className={cn(
          'text-primary-ink shrink-0 text-xs font-medium whitespace-nowrap underline underline-offset-2',
          'hover:decoration-2 focus-visible:ring-primary/60 rounded-sm focus-visible:ring-[3px] focus-visible:outline-none',
        )}
      >
        Chi tiết
      </Link>
    </span>
  );
}
